// src/components/clients/dependants/DependantFormPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, User } from 'lucide-react';
import { clientService } from '@services/clientService';
import CustomSelect from '../../shared/CustomSelect';

const DependantFormPage = () => {
    const { clientId, dependantId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!dependantId;

    const [loading, setLoading] = useState(false);
    const [fetchingDependant, setFetchingDependant] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [client, setClient] = useState(null);

    const [formData, setFormData] = useState({
        clientId: clientId,
        firstName: '',
        middleName: '',
        lastName: '',
        relationship: '',
        dateOfBirth: '',
        gender: '',
        nationalIdNumber: '',
        birthCertificateNumber: '',
        passportNumber: '',
        maritalStatus: '',
        isStudent: false,
        isActive: true,
        phone: '',
        email: '',
        address: ''
    });

    const [errors, setErrors] = useState({});

    const relationships = [
        'SPOUSE', 'SON', 'DAUGHTER', 'FATHER', 'MOTHER',
        'BROTHER', 'SISTER', 'GRANDFATHER', 'GRANDMOTHER',
        'GRANDSON', 'GRANDDAUGHTER', 'UNCLE', 'AUNT',
        'NEPHEW', 'NIECE', 'COUSIN', 'GUARDIAN', 'OTHER'
    ];

    const genders = ['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY'];

    const maritalStatuses = [
        'SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED', 'ENGAGED'
    ];

    // Fetch client details to display in header
    useEffect(() => {
        fetchClientInfo();
    }, [clientId]);

    // Fetch dependant data if editing
    useEffect(() => {
        if (isEditing && dependantId) {
            fetchDependantData();
        }
    }, [dependantId, isEditing]);

    const fetchClientInfo = async () => {
        try {
            const response = await clientService.getById(clientId);
            const clientData = response.data || response;
            setClient(clientData);
        } catch (error) {
            console.error('Error fetching client info:', error);
        }
    };

    const fetchDependantData = async () => {
        try {
            setFetchingDependant(true);
            const response = await clientService.dependants.getById(dependantId);

            if (response.success === false) {
                throw new Error(response.message || 'Failed to fetch dependant');
            }

            const dependant = response.data || response;

            setFormData({
                clientId: clientId,
                firstName: dependant.firstName || '',
                middleName: dependant.middleName || '',
                lastName: dependant.lastName || '',
                relationship: dependant.relationship || '',
                dateOfBirth: dependant.dateOfBirth || '',
                gender: dependant.gender || '',
                nationalIdNumber: dependant.nationalIdNumber || '',
                birthCertificateNumber: dependant.birthCertificateNumber || '',
                passportNumber: dependant.passportNumber || '',
                maritalStatus: dependant.maritalStatus || '',
                isStudent: dependant.isStudent || false,
                isActive: dependant.isActive !== false,
                phone: dependant.phone || '',
                email: dependant.email || '',
                address: dependant.address || ''
            });
        } catch (error) {
            console.error('Error fetching dependant:', error);
            setErrorMessage(`Failed to load dependant data: ${error.message}`);
            setShowErrorMessage(true);
            setTimeout(() => {
                navigate(`/clients/${clientId}`);
            }, 3000);
        } finally {
            setFetchingDependant(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.relationship) {
            newErrors.relationship = 'Relationship is required';
        }
        if (!formData.dateOfBirth) {
            newErrors.dateOfBirth = 'Date of birth is required';
        }
        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }

        // Email validation if provided
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validate()) {
            setLoading(true);
            setShowErrorMessage(false);
            setShowSuccessMessage(false);

            try {
                let response;
                if (isEditing) {
                    response = await clientService.dependants.update(dependantId, formData);
                } else {
                    response = await clientService.dependants.create(formData);
                }

                if (response.success) {
                    setShowSuccessMessage(true);
                    setTimeout(() => {
                        navigate(`/clients/${clientId}`, { state: { activeTab: 'dependants' } });
                    }, 1500);
                } else {
                    throw new Error(response.message || 'Failed to save dependant');
                }
            } catch (error) {
                console.error('Error saving dependant:', error);
                setErrorMessage(error.message || 'Failed to save dependant');
                setShowErrorMessage(true);
            } finally {
                setLoading(false);
            }
        }
    };

    if (fetchingDependant) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading dependant data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="p-6">
                <div className="space-y-6">
                    {/* Header */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => navigate(`/clients/${clientId}`, { state: { activeTab: 'dependants' } })}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {isEditing ? 'Edit Dependant' : 'Add New Dependant'}
                                    </h1>
                                    {client && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            For client: {client.firstName} {client.lastName} ({client.clientCode})
                                        </p>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {isEditing ? 'Update' : 'Save'} Dependant
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Success/Error Messages */}
                    {showSuccessMessage && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm text-green-800">
                                Dependant {isEditing ? 'updated' : 'created'} successfully!
                            </p>
                        </div>
                    )}

                    {showErrorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-800">{errorMessage}</p>
                        </div>
                    )}

                    {/* Form */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Information */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    Basic Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.firstName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Enter first name"
                                        />
                                        {errors.firstName && (
                                            <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Middle Name
                                        </label>
                                        <input
                                            type="text"
                                            name="middleName"
                                            value={formData.middleName}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter middle name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.lastName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Enter last name"
                                        />
                                        {errors.lastName && (
                                            <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Personal Details */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    Personal Details
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Relationship <span className="text-red-500">*</span>
                                        </label>
                                        <CustomSelect
                                            value={formData.relationship}
                                            onChange={(val) => {
                                                setFormData(prev => ({ ...prev, relationship: val }));
                                                if (errors.relationship) {
                                                    setErrors(prev => ({ ...prev, relationship: '' }));
                                                }
                                            }}
                                            options={relationships.map(rel => ({
                                                value: rel,
                                                label: rel.replace('_', ' ')
                                            }))}
                                            placeholder="Select Relationship"
                                            error={!!errors.relationship}
                                            searchable
                                        />
                                        {errors.relationship && (
                                            <p className="text-red-500 text-xs mt-1">{errors.relationship}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date of Birth <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="dateOfBirth"
                                            value={formData.dateOfBirth}
                                            onChange={handleChange}
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        />
                                        {errors.dateOfBirth && (
                                            <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Gender <span className="text-red-500">*</span>
                                        </label>
                                        <CustomSelect
                                            value={formData.gender}
                                            onChange={(val) => {
                                                setFormData(prev => ({ ...prev, gender: val }));
                                                if (errors.gender) {
                                                    setErrors(prev => ({ ...prev, gender: '' }));
                                                }
                                            }}
                                            options={genders.map(gender => ({
                                                value: gender,
                                                label: gender.replace('_', ' ')
                                            }))}
                                            placeholder="Select Gender"
                                            error={!!errors.gender}
                                        />
                                        {errors.gender && (
                                            <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Marital Status
                                        </label>
                                        <CustomSelect
                                            value={formData.maritalStatus}
                                            onChange={(val) => {
                                                setFormData(prev => ({ ...prev, maritalStatus: val }));
                                            }}
                                            options={maritalStatuses.map(status => ({
                                                value: status,
                                                label: status
                                            }))}
                                            placeholder="Select Status"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Identification Numbers */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    Identification
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            National ID Number
                                        </label>
                                        <input
                                            type="text"
                                            name="nationalIdNumber"
                                            value={formData.nationalIdNumber}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter national ID"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Birth Certificate Number
                                        </label>
                                        <input
                                            type="text"
                                            name="birthCertificateNumber"
                                            value={formData.birthCertificateNumber}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter birth certificate number"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Passport Number
                                        </label>
                                        <input
                                            type="text"
                                            name="passportNumber"
                                            value={formData.passportNumber}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter passport number"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    Contact Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="+254 700 000 000"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.email ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="email@example.com"
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                        )}
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Address
                                        </label>
                                        <textarea
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter full address"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status Checkboxes */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    Status
                                </h4>
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="isStudent"
                                            checked={formData.isStudent}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Is Student</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DependantFormPage;
